import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  probeInstance,
  type InstanceProbeReport,
  type LandingId,
  type ResolvedLocale,
  type SponsorCategory,
  type VisiousSettings,
} from '@visious/core';
import { activeLocale, fmt, text } from '../../strings';
import { useHistoryMutations } from '../../history/mutations';
import { getPlatform, invalidatePlatform } from '../../platform/instance';
import { clearPopularDisabled, usePopularDisabled } from '../../platform/popularCapability';
import { createProbeFetch } from '../../platform/probe';
import { nowEpochSec, signOutLocally } from '../../platform/session';
import { refreshFromInstance, useSettingsSync } from '../../platform/settingsSync';
import { resetSettings, useSettings, writeSettings } from '../../platform/settings';
import { clearWatchLayout } from '../../platform/watchLayout';
import { clearChannelMeta } from '../../platform/channelMetaStore';
import { clearVideoMeta } from '../../platform/videoMetaStore';
import { clearWatchProgress, watchProgressCount } from '../../platform/watchProgressStore';
import { clearPlaylistOrderMemory } from '../../playlists/orderMemory';
import { describeHistoryWriteProblem } from '../history/model';
import type { ProblemCopy } from '../../api/problem';
import {
  audioPatch,
  canSwitchInstance,
  deArrowApiUrlPatch,
  deArrowEnabledPatch,
  deArrowReplaceThumbnailsPatch,
  deArrowThumbnailApiUrlPatch,
  describePreferencesProblem,
  forceAvcPatch,
  youtubeEmbedEnabledPatch,
  instanceFieldState,
  instanceSourceCopy,
  landingPatch,
  menuItemPatch,
  qualityPatch,
  regionPatch,
  speedPatch,
  sponsorApiFieldState,
  sponsorApiUrlPatch,
  sponsorCategoryColorPatch,
  sponsorCategoryItemPatch,
  sponsorEnabledPatch,
  returnYoutubeDislikeApiUrlPatch,
  returnYoutubeDislikeEnabledPatch,
  peerTubeApiUrlPatch,
  peerTubeEnabledPatch,
  peerTubeModePatch,
  peerTubeSearchTargetPatch,
  subtitlePatch,
  syncStateCopy,
  syncTone,
  themePatch,
  uiLanguagePatch,
  type InstanceFieldState,
  type SettingsScreenState,
} from './model';

/**
 * The Settings page's bindings — §8's Settings page (sync model revised 2026-08-01).
 *
 * Since the revision the page no longer syncs anything itself: `platform/settingsSync.ts` pulls the
 * account's preferences on every signed-in boot and pushes after any local write, so a setter here is
 * exactly `writeSettings(patch)` and the sync section renders that module's snapshot. What is left is
 * genuinely the page's — the instance card with its probe, the one text field with state of its own,
 * and the clear/reset controls.
 */

export interface SettingsBinding {
  readonly state: SettingsScreenState;
  /**
   * One setter per control, rather than one `set(patch)` the view builds a patch for. R13's line is
   * that the `.tsx` renders props — and "which arm of `VideoQualityPref` does 1080 build" is a decision,
   * not a rendering. The patch builders are in `model.ts`; these are how the view reaches them.
   */
  readonly setQuality: (id: string) => void;
  readonly setSpeed: (id: string) => void;
  readonly setAudioLanguage: (tag: string) => void;
  readonly setSubtitleLanguage: (tag: string) => void;
  readonly setForceAvc: (on: boolean) => void;
  readonly setAutoplay: (on: boolean) => void;
  /** The Watch on menu's YouTube swap row — local-only, an escape hatch rather than a default. */
  readonly setYoutubeEmbedEnabled: (on: boolean) => void;
  /** The grid hover previews (2026-08-02) — local-only, like every switch without a field. */
  readonly setGridPreviews: (on: boolean) => void;
  /** The corner player a navigation away from a playing video leaves behind — local-only. */
  readonly setMiniPlayer: (on: boolean) => void;
  /** The trending section on the popular page — where the retired Trending screen's rows went. */
  readonly setTrendingOnPopular: (on: boolean) => void;
  readonly setUseStableVolume: (on: boolean) => void;
  readonly setMirrorWatchHistory: (on: boolean) => void;
  readonly setResumePlayback: (on: boolean) => void;
  readonly setTheme: (id: string) => void;
  /** 4e. Applying is a separate act from storing, the same pair `platform/theme.ts` argues for. */
  readonly setUiLanguage: (id: string) => void;
  /** Which language the interface resolved to, and why — the Language row's own subject. */
  readonly locale: ResolvedLocale;
  readonly setLanding: (id: string) => void;
  /** The Menu row's multi-select — flip one nav destination between shown and hidden, locally. */
  readonly toggleMenuItem: (id: LandingId) => void;
  /** Whether the instance has popular switched off — Popular's toggle is disabled while it is. */
  readonly popularDisabled: boolean;
  readonly setRegion: (id: string) => void;
  /** The Integrations tab — one setter per control, exactly like the rest of the page. */
  readonly setSponsorEnabled: (on: boolean) => void;
  /**
   * The service-address field: text state of its own for the page-size field's reason — a URL is
   * typed through a dozen invalid prefixes, and committing those would store junk twelve times.
   * Empty commits as `null`, the official service.
   */
  readonly sponsorApiText: string;
  readonly setSponsorApiText: (value: string) => void;
  readonly setSponsorCategoryColor: (id: SponsorCategory, value: string) => void;
  /** One press in a category's menu — a behaviour sets, a mark toggles (`sponsorCategoryItemPatch`). */
  readonly toggleSponsorCategoryItem: (id: SponsorCategory, itemId: string) => void;
  /** The DeArrow card (2026-08-02) — SponsorBlock's grammar, two addresses instead of one. */
  readonly setDeArrowEnabled: (on: boolean) => void;
  readonly setDeArrowReplaceThumbnails: (on: boolean) => void;
  readonly deArrowApiText: string;
  readonly setDeArrowApiText: (value: string) => void;
  readonly deArrowThumbApiText: string;
  readonly setDeArrowThumbApiText: (value: string) => void;
  /** The Return YouTube Dislike card — SponsorBlock's simplest grammar, one address. */
  readonly setReturnYoutubeDislikeEnabled: (on: boolean) => void;
  readonly returnYoutubeDislikeApiText: string;
  readonly setReturnYoutubeDislikeApiText: (value: string) => void;
  /** The PeerTube card — one address, which search target it is asked, which player it plays in. */
  readonly setPeerTubeEnabled: (on: boolean) => void;
  readonly peerTubeApiText: string;
  readonly setPeerTubeApiText: (value: string) => void;
  readonly setPeerTubeSearchTarget: (id: string) => void;
  readonly setPeerTubeMode: (id: string) => void;
  readonly setInstanceText: (value: string) => void;
  /** §3's five steps against the typed address. */
  readonly check: () => void;
  readonly probing: boolean;
  readonly report: InstanceProbeReport | null;
  readonly probeProblem: string | null;
  readonly canSwitch: boolean;
  readonly switchInstance: () => void;
  readonly resetInstance: () => void;
  /** The Data tab: the five clear buttons, each behind the app's one confirmation grammar. */
  readonly data: DataActions;
}

export interface DataActions {
  /** Whether there is a session — the auth button hides without one. */
  readonly signedIn: boolean;
  /** The instance-side half of a clear in flight (the history `DELETE`). */
  readonly busy: boolean;
  /** A refused instance-side clear, as a sentence. The local halves cannot fail. */
  readonly problem: ProblemCopy | null;
  /** 4b — how many playheads are stored. Signed out with none, the history button has no work. */
  readonly storedPositions: number;
  /** Account watch history (one `DELETE`) + this browser's watch positions. Not the token. */
  readonly clearWatchHistory: () => void;
  /** Settings and UI state back to the defaults; synced values return from the account. */
  readonly clearLocalState: () => void;
  /** Remembered titles, capability facts, the query cache — and a fresh pull of the account copy. */
  readonly clearLocalCache: () => void;
  /** Sign this browser out. The instance session survives (`signOutLocally` says why). */
  readonly clearLocalAuth: () => void;
  /** All four, in the order that keeps the token alive until the history `DELETE` has gone out. */
  readonly clearAll: () => void;
}

export function useSettingsScreen(): SettingsBinding {
  const settings = useSettings();
  const sync = useSettingsSync();
  const client = useQueryClient();
  const popularDisabled = usePopularDisabled();

  const [instanceUrl, setInstanceUrl] = useState<string>('');
  const [instanceText, setInstanceText] = useState<string>('');
  const [instanceSource, setInstanceSource] = useState<SettingsScreenState['instance']['source']>(null);
  const [instanceHost, setInstanceHost] = useState<string | null>(null);

  const [report, setReport] = useState<InstanceProbeReport | null>(null);
  const [probing, setProbing] = useState(false);
  const [probeProblem, setProbeProblem] = useState<string | null>(null);

  /*
   * Read at first render rather than in an effect: the count is a synchronous read of a module-level
   * value the store keeps in memory, so there is nothing to wait for — and setting state from an effect
   * for a value that was already available is a cascading render for no reason.
   */
  const [storedPositions, setStoredPositions] = useState(() => watchProgressCount());
  const [dataProblem, setDataProblem] = useState<ProblemCopy | null>(null);
  const historyMutations = useHistoryMutations();

  const probeAbort = useRef<AbortController | null>(null);

  // The platform boots asynchronously (§3 step 3 is a fetch), so the instance arrives a tick late —
  // the same reason the shell's host does. Decorative until it lands rather than blocking the page.
  useEffect(() => {
    let live = true;
    void getPlatform().then((boot) => {
      if (!live || !boot.ok) return;
      setInstanceUrl(boot.platform.config.instanceUrl);
      setInstanceText(boot.platform.config.instanceUrl);
      setInstanceSource(boot.platform.instanceSource);
      setInstanceHost(new URL(boot.platform.config.instanceUrl).host);
    });
    return () => {
      live = false;
    };
  }, []);

  // Writing is all a setter does: `platform/settingsSync.ts` watches the store and pushes for itself,
  // so the page cannot forget to — and a failed push surfaces in the module's snapshot below.
  const set = useCallback((patch: Partial<VisiousSettings>) => {
    writeSettings(patch);
  }, []);

  const [sponsorApiTextState, setSponsorApiTextState] = useState<string | null>(null);
  const setSponsorApiText = useCallback(
    (value: string) => {
      setSponsorApiTextState(value);
      // Committed when the text is a usable address — or empty, which *is* a value: the official
      // service. A half-typed URL stays in the field with its problem shown and stores nothing.
      const field = sponsorApiFieldState(value);
      if (field.problem === null) set(sponsorApiUrlPatch(settings, field.normalized));
    },
    [set, settings],
  );

  // DeArrow's two address fields — the SponsorBlock field's commit rule, twice.
  const [deArrowApiTextState, setDeArrowApiTextState] = useState<string | null>(null);
  const setDeArrowApiText = useCallback(
    (value: string) => {
      setDeArrowApiTextState(value);
      const field = sponsorApiFieldState(value);
      if (field.problem === null) set(deArrowApiUrlPatch(settings, field.normalized));
    },
    [set, settings],
  );
  const [deArrowThumbApiTextState, setDeArrowThumbApiTextState] = useState<string | null>(null);
  const setDeArrowThumbApiText = useCallback(
    (value: string) => {
      setDeArrowThumbApiTextState(value);
      const field = sponsorApiFieldState(value);
      if (field.problem === null) set(deArrowThumbnailApiUrlPatch(settings, field.normalized));
    },
    [set, settings],
  );

  // Return YouTube Dislike's address field — the SponsorBlock field's commit rule again.
  const [returnYoutubeDislikeApiTextState, setReturnYoutubeDislikeApiTextState] = useState<
    string | null
  >(null);
  const setReturnYoutubeDislikeApiText = useCallback(
    (value: string) => {
      setReturnYoutubeDislikeApiTextState(value);
      const field = sponsorApiFieldState(value);
      if (field.problem === null) set(returnYoutubeDislikeApiUrlPatch(settings, field.normalized));
    },
    [set, settings],
  );

  // PeerTube's address field — the same commit rule once more.
  const [peerTubeApiTextState, setPeerTubeApiTextState] = useState<string | null>(null);
  const setPeerTubeApiText = useCallback(
    (value: string) => {
      setPeerTubeApiTextState(value);
      const field = sponsorApiFieldState(value);
      if (field.problem === null) set(peerTubeApiUrlPatch(settings, field.normalized));
    },
    [set, settings],
  );

  const check = useCallback(() => {
    const field = instanceFieldState(instanceText, instanceUrl);
    if (field.normalized === null) return;

    probeAbort.current?.abort();
    const controller = new AbortController();
    probeAbort.current = controller;

    setProbing(true);
    setReport(null);
    setProbeProblem(null);
    void probeInstance(createProbeFetch(field.normalized, controller.signal))
      .then((result) => {
        if (controller.signal.aborted) return;
        setReport(result);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // `probeInstance` does not throw for a refused request — every failure is a `ProbeFetchResult`.
        // Reaching here means something else broke, and saying so beats an empty report.
        setProbeProblem(text().settings.instance.checkFailed);
      })
      .finally(() => {
        if (!controller.signal.aborted) setProbing(false);
      });
  }, [instanceText, instanceUrl]);

  /**
   * Switch, and then **reload**.
   *
   * `invalidatePlatform()` exists for this and is not enough on its own: the QueryClient holds the old
   * instance's feeds and channels, the session store holds a credential keyed to the old origin, and the
   * player may be mounted against a manifest from it. A reload is the only thing that guarantees none of
   * that survives — and it is also what runs R9's `clearOtherInstances()`, which is the step that makes
   * the warning true rather than approximate.
   */
  const switchInstance = useCallback(() => {
    const field = instanceFieldState(instanceText, instanceUrl);
    if (field.normalized === null) return;
    try {
      globalThis.localStorage.setItem('visious.instance', field.normalized);
    } catch {
      // Storage refused, so the switch cannot outlive this page load. Not worth a reload that would
      // land back on the old instance and look like the button did nothing.
      setProbeProblem(text().settings.instance.saveRefused);
      return;
    }
    client.clear();
    invalidatePlatform();
    // A fact about the old instance must not hide the new one's Popular item (popularCapability.ts).
    clearPopularDisabled();
    // To the Instance tab rather than home: the reader is mid-task, and the probe result for the new
    // instance is the thing they will want to see.
    globalThis.location.assign('/settings/instance');
  }, [client, instanceText, instanceUrl]);

  const resetInstance = useCallback(() => {
    try {
      globalThis.localStorage.removeItem('visious.instance');
    } catch {
      setProbeProblem(text().settings.instance.clearRefused);
      return;
    }
    client.clear();
    invalidatePlatform();
    clearPopularDisabled();
    globalThis.location.assign('/settings/instance');
  }, [client]);

  /* ----------------------------------------------------------------------------------------------
   * The Data tab's five clears. Two reusable halves first, because "clear all" is the other four in
   * a specific order — the token has to outlive the history `DELETE` it authorises.
   * -------------------------------------------------------------------------------------------- */

  const signedIn = sync.state !== 'signed-out';

  /** The browser-local settings and UI state — everything under #2, with no instance involvement. */
  const wipeLocalSettings = useCallback(() => {
    resetSettings();
    clearWatchLayout();
    clearPlaylistOrderMemory();
  }, []);

  /** The caches — remembered titles, the capability fact, and the query cache. */
  const wipeCaches = useCallback(() => {
    clearChannelMeta();
    clearVideoMeta();
    clearPopularDisabled();
    client.clear();
  }, [client]);

  /**
   * Watch history: the account's list (**one** `DELETE` — the bare path wipes it wholesale,
   * api-traps) and this browser's watch positions. The token and the UI state stay.
   */
  const clearWatchHistory = useCallback(() => {
    setDataProblem(null);
    clearWatchProgress();
    setStoredPositions(watchProgressCount());
    if (!signedIn) return;
    void historyMutations.clear().then((result) => {
      if (!result.ok) setDataProblem(describeHistoryWriteProblem(result.problem, 'clear'));
    });
  }, [historyMutations, signedIn]);

  const clearLocalState = useCallback(() => {
    setDataProblem(null);
    wipeLocalSettings();
    // Signed in, the defaults are not the last word: the account's values are, so they are re-pulled
    // rather than left to the next boot — otherwise the page would show defaults it believes synced.
    refreshFromInstance();
  }, [wipeLocalSettings]);

  const clearLocalCache = useCallback(() => {
    setDataProblem(null);
    wipeCaches();
    // The account-settings copy is a cache too, and this is its "clear": re-filled from the account,
    // because a cache of a living document has no empty state to clear *to*.
    refreshFromInstance();
  }, [wipeCaches]);

  const clearLocalAuth = useCallback(() => {
    setDataProblem(null);
    // The sync module watches the session, so it clears its own stamp and state on the way down.
    void signOutLocally();
  }, []);

  /** All of the above — and deliberately **not** the instance choice, which is connection, not data. */
  const clearAll = useCallback(() => {
    setDataProblem(null);
    clearWatchProgress();
    setStoredPositions(watchProgressCount());
    const rest = (): void => {
      wipeCaches();
      wipeLocalSettings();
      void signOutLocally();
    };
    if (!signedIn) {
      rest();
      return;
    }
    // The history `DELETE` first and the sign-out last: clearing the token first would strand the one
    // request that needs it. A refusal is reported, and the local clears still happen — they are the
    // reader's to make regardless of what the instance answered.
    void historyMutations.clear().then((result) => {
      if (!result.ok) setDataProblem(describeHistoryWriteProblem(result.problem, 'clear'));
      rest();
    });
  }, [historyMutations, signedIn, wipeCaches, wipeLocalSettings]);

  const field: InstanceFieldState = instanceFieldState(instanceText, instanceUrl);
  const now = nowEpochSec();

  return {
    state: {
      settings,
      instance: {
        url: instanceUrl,
        host: instanceHost,
        source: instanceSource,
        sourceText: instanceSource === null ? null : instanceSourceCopy()[instanceSource],
        field,
      },
      sync: {
        state: sync.state,
        tone: syncTone(sync.state),
        copy: syncStateCopy()[sync.state],
        lastSyncedText:
          sync.lastSyncedEpochSec === null ? null : fmt().published(sync.lastSyncedEpochSec, now),
        busy: sync.busy,
        problem: sync.problem === null ? null : describePreferencesProblem(sync.problem),
      },
    },
    setQuality: useCallback((id: string) => set(qualityPatch(settings, id)), [set, settings]),
    setSpeed: useCallback((id: string) => set(speedPatch(settings, id)), [set, settings]),
    setAudioLanguage: useCallback((tag: string) => set(audioPatch(settings, tag)), [set, settings]),
    setSubtitleLanguage: useCallback((tag: string) => set(subtitlePatch(settings, tag)), [set, settings]),
    setForceAvc: useCallback((on: boolean) => set(forceAvcPatch(settings, on)), [set, settings]),
    setYoutubeEmbedEnabled: useCallback(
      (on: boolean) => set(youtubeEmbedEnabledPatch(settings, on)),
      [set, settings],
    ),
    setAutoplay: useCallback((on: boolean) => set({ autoplay: on }), [set]),
    setGridPreviews: useCallback((on: boolean) => set({ gridPreviews: on }), [set]),
    setMiniPlayer: useCallback((on: boolean) => set({ miniPlayer: on }), [set]),
    setTrendingOnPopular: useCallback((on: boolean) => set({ trendingOnPopular: on }), [set]),
    setUseStableVolume: useCallback((on: boolean) => set({ useStableVolume: on }), [set]),
    setMirrorWatchHistory: useCallback((on: boolean) => set({ mirrorWatchHistory: on }), [set]),
    /*
     * Turning it **off leaves the stored positions alone** rather than clearing them, and that is a
     * decision: destroying data as a side effect of a switch is the kind of thing a reader cannot undo
     * and did not ask for. Forgetting is its own button below, with the count next to it — and while the
     * switch is off nothing is recorded, resumed or drawn, so the data is inert as well as invisible.
     */
    setResumePlayback: useCallback((on: boolean) => set({ resumePlayback: on }), [set]),
    setTheme: useCallback((id: string) => set(themePatch(id)), [set]),
    // `writeSettings` itself applies the catalogue for a `uiLocale` patch (`platform/settings.ts`),
    // so this setter is as plain as the others.
    setUiLanguage: useCallback((id: string) => set(uiLanguagePatch(id)), [set]),
    locale: activeLocale(),
    setLanding: useCallback((id: string) => set(landingPatch(id)), [set]),
    // Local-only: menu visibility has no Invidious field; the sync module still pushes the shared
    // fields after the write — harmless, since the patch only touches `hiddenMenu`.
    toggleMenuItem: useCallback(
      (id: LandingId) => set(menuItemPatch(settings, id, settings.hiddenMenu.includes(id))),
      [set, settings],
    ),
    popularDisabled,
    setRegion: useCallback((id: string) => set(regionPatch(id)), [set]),
    // The Integrations tab. Local-only like `hiddenMenu` — the sync module's push after each write
    // only carries the mapped fields, so none of this reaches the instance.
    setSponsorEnabled: useCallback((on: boolean) => set(sponsorEnabledPatch(settings, on)), [set, settings]),
    sponsorApiText: sponsorApiTextState ?? settings.sponsorBlock.apiUrl ?? '',
    setSponsorApiText,
    setSponsorCategoryColor: useCallback(
      (id: SponsorCategory, value: string) => set(sponsorCategoryColorPatch(settings, id, value)),
      [set, settings],
    ),
    toggleSponsorCategoryItem: useCallback(
      (id: SponsorCategory, itemId: string) => set(sponsorCategoryItemPatch(settings, id, itemId)),
      [set, settings],
    ),
    setDeArrowEnabled: useCallback((on: boolean) => set(deArrowEnabledPatch(settings, on)), [set, settings]),
    setDeArrowReplaceThumbnails: useCallback(
      (on: boolean) => set(deArrowReplaceThumbnailsPatch(settings, on)),
      [set, settings],
    ),
    deArrowApiText: deArrowApiTextState ?? settings.deArrow.apiUrl ?? '',
    setDeArrowApiText,
    deArrowThumbApiText: deArrowThumbApiTextState ?? settings.deArrow.thumbnailApiUrl ?? '',
    setDeArrowThumbApiText,
    setReturnYoutubeDislikeEnabled: useCallback(
      (on: boolean) => set(returnYoutubeDislikeEnabledPatch(settings, on)),
      [set, settings],
    ),
    returnYoutubeDislikeApiText:
      returnYoutubeDislikeApiTextState ?? settings.returnYoutubeDislike.apiUrl ?? '',
    setReturnYoutubeDislikeApiText,
    setPeerTubeEnabled: useCallback(
      (on: boolean) => set(peerTubeEnabledPatch(settings, on)),
      [set, settings],
    ),
    peerTubeApiText: peerTubeApiTextState ?? settings.peerTube.apiUrl ?? '',
    setPeerTubeApiText,
    setPeerTubeSearchTarget: useCallback(
      (id: string) => set(peerTubeSearchTargetPatch(settings, id)),
      [set, settings],
    ),
    setPeerTubeMode: useCallback((id: string) => set(peerTubeModePatch(settings, id)), [set, settings]),
    setInstanceText,
    check,
    probing,
    report,
    probeProblem,
    canSwitch: canSwitchInstance({ field, report, probing }),
    switchInstance,
    resetInstance,
    data: {
      signedIn,
      busy: historyMutations.clearing,
      problem: dataProblem,
      storedPositions,
      clearWatchHistory,
      clearLocalState,
      clearLocalCache,
      clearLocalAuth,
      clearAll,
    },
  };
}
